import React, { useState, useEffect, useRef } from 'react';
import Dropdown from '../../Components/CustomDropdown/customdropdown';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
export default function ReportGenerator({ theme, data }) {
  const [reportType, setReportType] = useState('business');
  const [filters, setFilters] = useState({});
  const [preview, setPreview] = useState(null);
  const [dateFilters, setDateFilters] = useState({
    dateRange: {
      from: '',
      to: '',
    },
  });
  const safeTheme = theme || {
    bg: 'bg-slate-50',
  };

  const REPORT_TYPES = [
    { id: 'business', label: 'Business-wide audit report', icon: 'domain' },
    { id: 'customer', label: 'Customer-specific audit report', icon: 'person' },
    { id: 'agent', label: 'Team Member audit report', icon: 'badge' },
    { id: 'ecdd', label: 'ECDD / high-risk report', icon: 'warning' },
    { id: 'austrac', label: 'AUSTRAC spot-check pack', icon: 'gavel' },
    { id: 'external', label: 'External reviewer pack', icon: 'groups' },
    { id: 'board', label: 'Board / management pack', icon: 'insights' },
  ];
  const FILTER_CONFIG = [
    {
      key: 'entityType',
      label: 'Entity Type',
      icon: 'business',
      options: [
        { value: 'individual', label: 'Individual' },
        { value: 'company', label: 'Company' },
        { value: 'trust', label: 'Trust' },
        { value: 'smsf', label: 'SMSF' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      key: 'riskRating',
      label: 'Risk Rating',
      icon: 'warning',
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'prohibited', label: 'Prohibited' },
      ],
    },
    {
      key: 'cddStatus',
      label: 'CDD Status',
      icon: 'verified',
      options: [
        { value: 'complete', label: 'Complete' },
        { value: 'pending', label: 'Pending' },
        { value: 'missing', label: 'Missing' },
      ],
    },
    {
      key: 'ecddStatus',
      label: 'ECDD Status',
      icon: 'gpp_maybe',
      options: [
        { value: 'required', label: 'Required' },
        { value: 'completed', label: 'Completed' },
        { value: 'not_required', label: 'Not Required' },
      ],
    },
    {
      key: 'trainingStatus',
      label: 'Training Status',
      icon: 'school',
      options: [
        { value: 'complete', label: 'Complete' },
        { value: 'incomplete', label: 'Incomplete' },
      ],
    },
    {
      key: 'documentExpiry',
      label: 'Document Expiry',
      icon: 'event_busy',
      options: [
        { value: 'valid', label: 'Valid' },
        { value: 'expired', label: 'Expired' },
        { value: 'expiring_soon', label: 'Expiring Soon' },
      ],
    },
  ];

  return (
    <div className={`p-6 ${safeTheme.bg}`}>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Generate Audit Readiness Report
          </h1>
          <p className="text-sm text-gray-500">
            Generate compliance-ready reports for audit, AUSTRAC, and internal
            governance
          </p>
        </div>

        {/* <button className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700">
          <span className="material-symbols-outlined">history</span>
          View Previous Reports
        </button> */}
      </div>

      <div className="flex w-full flex-row gap-5">
        {/* REPORT TYPE */}
        <div className="bg-white p-5 rounded-2xl border shadow-sm">
          <SectionTitle icon="description" title="Report Type" />

          <div className="grid grid-cols-2 gap-3">
            {REPORT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setReportType(type.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition ${
                  reportType === type.id
                    ? 'bg-blue-50 border-[#375AE7]'
                    : 'hover:bg-gray-50'
                }`}
              >
                <span className="material-symbols-outlined text-[#375AE7]">
                  {type.icon}
                </span>
                <span className="text-sm font-medium text-left">
                  {type.label}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border shadow-sm">
          {/* FILTERS */}
          <SectionTitle icon="filter_list" title="Scope Filters" />
          <div className="grid grid-cols-4 gap-3">
            <DateRangeFilter
              label="Date Range"
              icon="calendar_month"
              value={filters.dateRange}
              onChange={(range) =>
                setFilters((prev) => ({
                  ...prev,
                  dateRange: {
                    startDate: range[0],
                    endDate: range[1],
                  },
                }))
              }
            />
            <FilterInput label="Office / Branch" icon="apartment" />
            <FilterInput label="State / Territory" icon="map" />
            <FilterInput label="Agent / Team Member" icon="badge" />
            <FilterInput label="Customer" icon="person" />
            {FILTER_CONFIG.map((filter) => (
              <FilterSelect
                key={filter.key}
                label={filter.label}
                icon={filter.icon}
                options={filter.options}
                value={filters[filter.key]}
                onChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    [filter.key]: val,
                  }))
                }
              />
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-4 mt-4">
        <div className="bg-white p-5 rounded-2xl border shadow-sm">
          <SectionTitle icon="preview" title="Report Preview" />

          {/* PREVIEW SUMMARY */}
          <div className="grid grid-cols-2 gap-4">
            <PreviewItem label="Entity" value="My Business Pty Ltd" />
            <PreviewItem label="Report Type" value={reportType} />
            <PreviewItem label="Period" value="01 Jan - 30 Jun 2026" />
            <PreviewItem label="Generated By" value="Compliance Officer" />
            <PreviewItem label="Customers Included" value="124" />
            <PreviewItem label="CDD Files" value="118" />
            <PreviewItem label="ECDD Files" value="22" />
            <PreviewItem label="Training Rate" value="92%" />
          </div>
        </div>
        {/* STATUS ALERT */}
        <div className="p-4 rounded-xl bg-red-50 border border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <span className="material-symbols-outlined text-red-600">
              warning
            </span>
            <p className="font-semibold text-red-700 text-sm">
              Missing Evidence Detected
            </p>
          </div>
          <p className="text-xs text-red-600">
            This report contains unresolved evidence gaps. Please review before
            sharing externally.
          </p>
        </div>

        {/* STATUS TAG */}
        <div className="mt-4 flex items-center gap-2">
          <span className="px-3 py-1 text-xs bg-amber-100 text-amber-700 rounded-full">
            Complete with Exceptions
          </span>
        </div>
      </div>
      {/* FOOTER ACTIONS */}
      <div className="mt-6 flex justify-between items-center bg-white p-4 rounded-2xl border shadow-sm">
        <div className="flex gap-3">
          <ExportButton icon="picture_as_pdf" label="PDF" />
          <ExportButton icon="table_view" label="Excel" />
          <ExportButton icon="folder_zip" label="ZIP" />
          <ExportButton icon="dataset" label="CSV" />
          <ExportButton icon="link" label="Secure Link" />
        </div>

        <button className="bg-[#375AE7] text-white px-6 py-2 rounded-xl hover:bg-[#375AE7] flex items-center gap-2">
          <span className="material-symbols-outlined">play_arrow</span>
          Generate Report
        </button>
      </div>
    </div>
  );
}
function SectionTitle({ icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="material-symbols-outlined text-[#375AE7]">{icon}</span>
      <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
    </div>
  );
}
function FilterInput({ label, icon }) {
  return (
    <div className="mb-3">
      <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
        <span className="material-symbols-outlined text-sm text-slate-400">
          {icon}
        </span>
        {label}
      </label>{' '}
      <div className="flex items-center border rounded-xl px-3 py-2 mt-1">
        <input
          className="w-full outline-none text-sm"
          placeholder={`Select ${label}`}
        />
      </div>
    </div>
  );
}
function FilterSelect({
  label,
  options,
  value,
  onChange,
  error,
  icon = 'tune',
}) {
  return (
    <div className="mb-4">
      {/* LABEL */}
      <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
        <span className="material-symbols-outlined text-sm text-slate-400">
          {icon}
        </span>
        {label}
      </label>

      {/* DROPDOWN */}
      <div>
        <Dropdown
          label=""
          options={options}
          onSelect={onChange}
          preselectedValue={value}
          parentClass="rounded-xl"
        />
      </div>

      {/* ERROR TEXT */}
      {error && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">error</span>
          {error}
        </p>
      )}
    </div>
  );
}
function PreviewItem({ label, value }) {
  return (
    <div className="p-3 bg-gray-50 rounded-xl border">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-gray-900">
        {value?.charAt(0).toUpperCase() + value?.slice(1)}
      </p>
    </div>
  );
}
function ExportButton({ icon, label }) {
  return (
    <button className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl hover:bg-gray-50">
      <span className="material-symbols-outlined text-gray-600">{icon}</span>
      {label}
    </button>
  );
}
function DateRangeFilter({ label, icon, value, onChange }) {
  return (
    <div className="mb-4">
      {/* Label */}
      <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
        <span className="material-symbols-outlined text-sm text-slate-400">
          {icon}
        </span>
        {label}
      </label>

      {/* Input wrapper */}
      <div className="mt-1 relative">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white shadow-sm transition hover:shadow-md focus-within:ring-2 focus-within:ring-blue-200">
          {/* icon */}
          <span className="material-symbols-outlined text-gray-400 text-sm">
            calendar_month
          </span>

          {/* IMPORTANT FIX */}
          <div className="flex-1 min-w-0">
            <DatePicker
              selectsRange
              startDate={value?.startDate}
              endDate={value?.endDate}
              onChange={(update) => onChange(update)}
              placeholderText="Select date range"
              className="w-full text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
              dateFormat="dd MMM yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>

          {/* Clear button FIXED (absolute feel, no layout shift) */}
          {(value?.startDate || value?.endDate) && (
            <button
              onClick={() => onChange([null, null])}
              className="flex-shrink-0 text-gray-400 hover:text-red-500 transition"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          )}
        </div>

        {/* helper */}
        <p className="text-[11px] text-gray-400 mt-1">
          You can select start and end date
        </p>
      </div>
    </div>
  );
}
